import { useState } from "react";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import SurveyDispatch from "../../models/surveyDispatch";
import { grey4, roseOrange } from "../../styles/theme";
import { ActivityType } from "../../types/actionPlan";
import { ActionPlanEvent } from "../ActionPlanActivity/ActionPlanEvent";
import { SurveyLaunchButton } from "../SurveyLaunchButton/SurveyLaunchButton";
import { SurveySettings } from "../SurveySettings/SurveySettings";
import { Button } from "../ui/Button/Button";
import { Modal } from "../ui/Modal/Modal";
import { Typography } from "../ui/Typography/Typography";
import { InfoIcon } from "../ui/icons/InfoIcon";
import { TrashIcon } from "../ui/icons/TrashIcon";
import "./ActionPlanActivity.scss";

interface ActionPlanActivityProps {
  activity: Activity;
  onDateChange: (activity: Activity, eventIndex: number, newDate: Date) => void;
  onSave: (activity: Activity) => void | Promise<void>;
  onDelete: (activity: Activity) => void | Promise<void>;
  surveyDispatch?: SurveyDispatch;
  onSurveyUpdate?: () => void;
  onClose?: () => void;
  isSettingUp?: boolean;
  dateChangeError?: string;
}

export const ActionPlanActivity = ({
  activity,
  onDateChange,
  onSave,
  onDelete,
  surveyDispatch,
  onSurveyUpdate,
  onClose,
  isSettingUp = false,
  dateChangeError,
}: ActionPlanActivityProps) => {
  const { t } = useTranslation("actionPlanPage");
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const showSurveySettings =
    surveyDispatch && activity?.activityType === ActivityType.Survey;

  const showNewSurveyButton =
    activity?.activityType === ActivityType.ScheduleSurvey && !isSettingUp;

  const getActivityDetails = (): [string, string, string] => {
    if (activity?.isTrainingActivity()) {
      return [
        activity?.label ?? "",
        activity?.extraProperties?.training_module?.description || "",
        t("timeline.training.info"),
      ];
    } else if (showSurveySettings) {
      return [
        t(`timeline.editSurvey.title`),
        "",
        t("timeline.editSurvey.info"),
      ];
    }
    return [t(`timeline.planSurvey.title`), t("timeline.planSurvey.info"), ""];
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError("");
    try {
      await onDelete(activity);
      setConfirmationModalOpen(false);
      onClose?.();
    } catch {
      setError(t("timeline.errors.save") || "");
      setConfirmationModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError("");
    try {
      await onSave(activity);
      onClose?.();
    } catch {
      setError(t("timeline.errors.save") || "");
    } finally {
      setIsLoading(false);
    }
  };

  const [title, description, info] = getActivityDetails();

  const moduleId = activity?.extraProperties?.training_module?.id;
  const baseHref = activity?.href || "";
  const activityHref = moduleId
    ? `${baseHref}${baseHref.includes("?") ? "&" : "?"}module=${moduleId}`
    : baseHref;

  return (
    <div className="ActionPlanActivity">
      <Typography desktop="h2" className="ActionPlanActivity__title">
        {title}
      </Typography>
      {description && <Typography desktop="body1">{description}</Typography>}
      {!isSettingUp && info && (
        <div className="ActionPlanActivity__info">
          <InfoIcon size={48} color={grey4} />
          <Typography desktop="body2" fontStyle="italic" color="secondary">
            <Trans
              i18nKey={info}
              components={{
                u: <u />,
                anchor: (
                  <Link to={activityHref} target="_blank" rel="noreferrer" />
                ),
              }}
            />
          </Typography>
        </div>
      )}
      {showSurveySettings ? (
        <SurveySettings
          dispatch={surveyDispatch}
          onSurveyUpdate={onSurveyUpdate}
        />
      ) : (
        <div className="ActionPlanActivity__events">
          {activity?.events.map((event, index) => {
            const description = event.extraProperties?.touchpoint?.description;
            return (
              <ActionPlanEvent
                key={index}
                event={event}
                description={description}
                onDateChange={(newDate) =>
                  onDateChange(activity, index, newDate)
                }
              />
            );
          })}
        </div>
      )}
      {dateChangeError && (
        <Typography
          desktop="body2"
          color="error"
          className="ActionPlanActivity__error"
        >
          {dateChangeError}
        </Typography>
      )}
      {!showSurveySettings && (
        <div className="ActionPlanActivity__actions">
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmationModalOpen(true)}
            small
            disabled={isLoading}
            className="ActionPlanActivity__actions__button"
            data-testid="delete-activity-btn"
          >
            <TrashIcon color={roseOrange} />
            {isSettingUp
              ? t("timeline.actions.remove")
              : t("timeline.actions.delete")}
          </Button>
          {showNewSurveyButton && <SurveyLaunchButton color="info" />}
          <Button
            variant="contained"
            color="info"
            onClick={handleSave}
            small
            disabled={isLoading || !!dateChangeError}
            loading={isLoading}
            data-testid="save-activity-btn"
          >
            {isSettingUp
              ? t("timeline.actions.done")
              : t("timeline.actions.save")}
          </Button>
        </div>
      )}
      {error && (
        <Typography
          desktop="body2"
          color="error"
          className="ActionPlanActivity__error"
        >
          {error}
        </Typography>
      )}
      <Modal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        title={
          <Typography tagVariant="p" desktop="h3" weight="bold">
            {t("timeline.deleteConfirmation.title")}
          </Typography>
        }
      >
        <Typography desktop="body1">
          {t("timeline.deleteConfirmation.description", {
            moduleName: activity?.label || "",
          })}
        </Typography>
        <div className="ActionPlanActivity__modal__actions">
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setConfirmationModalOpen(false)}
            disabled={isLoading}
            data-testid="cancel-delete-activity-btn"
          >
            {t("timeline.deleteConfirmation.no")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDelete}
            disabled={isLoading}
            loading={isLoading}
            data-testid="confirm-delete-activity-btn"
          >
            {t("timeline.deleteConfirmation.yes")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
